import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import LoadingSpinner from '../LoadingSpinner';

/**
 * ProtectedRoute component that wraps routes requiring authentication
 * Redirects to login page if user is not authenticated
 * Preserves the intended destination URL for post-login redirect
 * 
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to render if authenticated
 * @returns {React.ReactElement} Protected content or redirect to login
 */
const ProtectedRoute = ({ children, ...props }) => {
  const { isAuthenticated, loading, user } = useAuth();
  const location = useLocation();

  // Show loading spinner while checking authentication status
  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh'
      }}>
        <LoadingSpinner />
      </div>
    );
  }

  // If not authenticated, redirect to login with return URL
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check for role-based access
  if (props.allowedRoles && !props.allowedRoles.includes(user?.role)) {
    // If user has a specific role dashboard, redirect there
    if (user?.role === 'institution_admin') return <Navigate to="/dashboard/institution" replace />;
    if (user?.role === 'teacher') return <Navigate to="/dashboard/teacher" replace />;
    if (user?.role === 'super_admin') return <Navigate to="/admin" replace />;
    if (user?.role === 'student') return <Navigate to="/dashboard" replace />;

    // Fallback to home instead of dashboard to avoid loops if dashboard is restricted
    return <Navigate to="/" replace />;
  }

  // User is authenticated, render the protected content
  return children;
};

export default ProtectedRoute;
